import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import supabase from '../../supabase/supabase';
import EnhancedInventoryAdjustment from '../inventory/EnhancedInventoryAdjustment';
import SalesListings from '../sales/SalesListings';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiEdit3, FiTrash2, FiPackage, FiDollarSign, FiBarChart3, FiTag, FiImage, FiShoppingBag, FiExternalLink, FiCamera, FiBox, FiX, FiChevronLeft, FiChevronRight } = FiIcons;

const ProductDetail = () => {
  const { id } = useParams();
  const { getProductById, updateProduct, deleteProduct } = useInventory();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Camera States
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const product = getProductById(id);

  useEffect(() => {
    if (product) {
      setEditData({ ...product, images: product.images || [] });
    }
  }, [product, isEditing]);

  useEffect(() => {
    return () => stopCamera();
  }, []);

  useEffect(() => {
    if (showCamera && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch(e => console.error("Error playing video:", e));
    }
  }, [showCamera, cameraStream]);

  if (!product) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Product not found</p>
        <button onClick={() => navigate('/products')} className="mt-4 text-blue-600 hover:text-blue-700">Back to Products</button>
      </div>
    );
  }

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        .catch(() => navigator.mediaDevices.getUserMedia({ video: true }));
      setCameraStream(stream);
      setShowCamera(true);
    } catch (err) {
      toast.error("Could not access camera.");
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      canvas.toBlob(async (blob) => {
        if (blob) {
          await uploadImage(blob);
        }
      }, 'image/jpeg', 0.8);
    }
  };

  const uploadImage = async (fileOrBlob) => {
    setUploadingImage(true);
    try {
      const fileName = `${id}/${Date.now()}.jpg`;
      const { error } = await supabase.storage.from('product-images').upload(fileName, fileOrBlob);
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('product-images').getPublicUrl(fileName);
      
      setEditData(prev => ({
        ...prev,
        images: [...(prev.images || []), publicUrl]
      }));
      toast.success('Photo added');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setEditData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    const success = await updateProduct(id, editData);
    if (success) {
      setIsEditing(false);
      stopCamera();
    }
  };

  const images = isEditing ? (editData.images || []) : (product.images || []);
  const stockStatus = product.quantity === 0 ? { label: 'Out of Stock', color: 'red' } : 
                     product.quantity <= 10 ? { label: 'Low Stock', color: 'yellow' } : 
                     { label: 'In Stock', color: 'green' };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate('/products')} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"><SafeIcon icon={FiArrowLeft} className="h-5 w-5" /></button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Product Details</h1>
            <p className="text-gray-600">ID: {product.localSku}</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button onClick={() => setIsEditing(!isEditing)} className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <SafeIcon icon={isEditing ? FiX : FiEdit3} className="h-4 w-4 mr-2" /> {isEditing ? 'Cancel' : 'Edit'}
          </button>
          <button onClick={() => { if(window.confirm('Delete product?')) { deleteProduct(id); navigate('/products'); }}} className="flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
            <SafeIcon icon={FiTrash2} className="h-4 w-4 mr-2" /> Delete
          </button>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Gallery Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Product Gallery</h3>
              {isEditing && !showCamera && (
                <button onClick={startCamera} className="flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg">
                  <SafeIcon icon={FiCamera} className="h-4 w-4 mr-2" /> Add Photo
                </button>
              )}
            </div>

            {showCamera && (
              <div className="relative bg-black rounded-lg overflow-hidden mb-4 aspect-video">
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                <div className="absolute bottom-4 inset-x-0 flex justify-center space-x-4">
                  <button onClick={capturePhoto} className="h-12 w-12 bg-white rounded-full border-4 border-gray-300 flex items-center justify-center shadow-lg">
                    <div className="h-10 w-10 bg-white rounded-full border-2 border-black" />
                  </button>
                  <button onClick={stopCamera} className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm">Close Camera</button>
                </div>
                <canvas ref={canvasRef} className="hidden" />
              </div>
            )}

            <div className="space-y-4">
              <div className="aspect-video relative bg-gray-100 rounded-xl overflow-hidden border border-gray-200">
                {images.length > 0 ? (
                  <>
                    <img src={images[activeImageIndex]} alt="" className="w-full h-full object-contain" />
                    {images.length > 1 && (
                      <div className="absolute inset-0 flex items-center justify-between p-4 pointer-events-none">
                        <button 
                          onClick={() => setActiveImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1))}
                          className="p-2 rounded-full bg-white/80 shadow-md pointer-events-auto hover:bg-white"
                        >
                          <SafeIcon icon={FiChevronLeft} className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => setActiveImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1))}
                          className="p-2 rounded-full bg-white/80 shadow-md pointer-events-auto hover:bg-white"
                        >
                          <SafeIcon icon={FiChevronRight} className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                    <SafeIcon icon={FiImage} className="h-16 w-16 mb-2" />
                    <p>No photos available</p>
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              <div className="flex flex-wrap gap-3">
                {images.map((img, idx) => (
                  <div key={idx} className="relative group">
                    <button 
                      onClick={() => setActiveImageIndex(idx)}
                      className={`h-20 w-20 rounded-lg overflow-hidden border-2 transition-all ${activeImageIndex === idx ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                    {isEditing && (
                      <button 
                        onClick={() => removeImage(idx)}
                        className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <SafeIcon icon={FiX} className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Info Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Product Information</h2>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${stockStatus.color === 'green' ? 'bg-green-100 text-green-800' : stockStatus.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                {stockStatus.label}
              </span>
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
                    <input type="text" value={editData.manufacturer} onChange={e => setEditData({...editData, manufacturer: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                    <input type="text" value={editData.model} onChange={e => setEditData({...editData, model: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" value={editData.name} onChange={e => setEditData({...editData, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={editData.description} onChange={e => setEditData({...editData, description: e.target.value})} rows={3} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">UPC</label>
                    <input type="text" value={editData.upc} onChange={e => setEditData({...editData, upc: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                    <input type="text" value={editData.localSku} onChange={e => setEditData({...editData, localSku: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                    <input type="number" step="0.01" value={editData.localPrice} onChange={e => setEditData({...editData, localPrice: parseFloat(e.target.value)})} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg">Cancel</button>
                  <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg">Save Changes</button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{product.name}</h3>
                  <p className="text-gray-600 text-lg">{product.manufacturer} - {product.model}</p>
                </div>
                {product.description && <p className="text-gray-600">{product.description}</p>}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Price</p>
                    <p className="font-bold text-gray-900">${product.localPrice?.toFixed(2)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Stock</p>
                    <p className="font-bold text-gray-900">{product.quantity}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">UPC</p>
                    <p className="font-bold text-gray-900">{product.upc || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Pallet</p>
                    <p className="font-bold text-gray-900">{product.pallet?.pallet_number || 'Unassigned'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <EnhancedInventoryAdjustment productId={id} />
          <SalesListings productId={id} />
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;